import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { GET_WAREHOUSE_INFO } from 'src/api/warehouses.api';
import { notify } from 'src/lib/notify';
import type { CreatePlaceInput, UpdatePlaceInput } from 'types/graphql';
import { useCreatePlace } from './useCreatePlace';
import { useUpdatePlace } from './useUpdatePlace';

/** The warehouse modal's place create, unchanged: a snackbar on success, a notify on failure; resolves the new id. */
export function useCreateWarehousePlace() {
  const { create } = useCreatePlace({
    onCompleted: () => {
      enqueueSnackbar('Place created successfully');
    },
    onError: (error) => {
      notify.error(`Failed to create place: ${error.message}`);
    },
  });

  return useCallback(
    async (input: CreatePlaceInput) => {
      const { data } = await create(input);
      return data?.res.id;
    },
    [create]
  );
}

/** The warehouse modal's place update, unchanged: refetches the warehouse page, snackbar on success, notify on failure. */
export function useUpdateWarehousePlace() {
  const { update } = useUpdatePlace({
    refetchQueries: [GET_WAREHOUSE_INFO],
    onCompleted: () => {
      enqueueSnackbar('Place updated successfully');
    },
    onError: (error) => {
      notify.error(`Failed to update place: ${error.message}`);
    },
  });

  return useCallback(
    async (id: number, input: UpdatePlaceInput) => {
      const { data } = await update({ id, input });
      return data?.updatePlace;
    },
    [update]
  );
}
